import React, { useEffect, useMemo, useState } from "react";
import { readCache, writeCache } from "../dataCache";
import MobileScrollArea from "./MobileScrollArea";

const API_URL = window.location.origin;

const DATE_PRESETS = [
  { label: "Today", type: "today" },
  { label: "Yesterday", type: "yesterday" },
  { label: "This week", type: "thisWeek" },
  { label: "Last week", type: "lastWeek" },
  { label: "This month", type: "thisMonth" },
  { label: "Last month", type: "lastMonth" },
  { label: "This year", type: "thisYear" },
  { label: "Last year", type: "lastYear" },
  { label: "Last 7 days", days: 7 },
  { label: "Last 14 days", days: 14 },
  { label: "Last 30 days", days: 30 },
  { label: "All time", type: "allTime" }
];

function safeNumber(value) {
  const number = Number(value || 0);
  return Number.isNaN(number) ? 0 : number;
}

function formatNumber(value) {
  return safeNumber(value).toLocaleString();
}

function safeDate(value) {
  const date = value ? new Date(value) : new Date();
  return Number.isNaN(date.getTime()) ? new Date() : date;
}

function formatDate(date) {
  return safeDate(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}

function formatDateTime(date) {
  return safeDate(date).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function createPresetRange(presetOrDays) {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);

  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  const preset =
    typeof presetOrDays === "number"
      ? { days: presetOrDays }
      : presetOrDays || { days: 30 };

  if (preset.type === "today") {
    return { start, end };
  }

  if (preset.type === "yesterday") {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
    return { start, end };
  }

  if (preset.type === "thisWeek") {
    const day = start.getDay() || 7;
    start.setDate(start.getDate() - day + 1);
    return { start, end };
  }

  if (preset.type === "lastWeek") {
    const day = start.getDay() || 7;
    start.setDate(start.getDate() - day - 6);
    end.setTime(start.getTime());
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  if (preset.type === "thisMonth") {
    start.setDate(1);
    return { start, end };
  }

  if (preset.type === "lastMonth") {
    start.setDate(1);
    start.setMonth(start.getMonth() - 1);
    end.setDate(0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  if (preset.type === "thisYear") {
    start.setMonth(0, 1);
    return { start, end };
  }

  if (preset.type === "lastYear") {
    start.setFullYear(start.getFullYear() - 1, 0, 1);
    end.setFullYear(end.getFullYear() - 1, 11, 31);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  if (preset.type === "allTime") {
    start.setFullYear(2000, 0, 1);
    return { start, end };
  }

  const days = Number(preset.days || 30);
  start.setDate(end.getDate() - days + 1);
  return { start, end };
}

function isSameDay(a, b) {
  if (!a || !b) return false;

  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isBetween(date, start, end) {
  if (!date || !start || !end) return false;

  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();

  return d >= s && d <= e;
}

function getValueByPath(obj, path) {
  return path.split(".").reduce((acc, key) => acc && acc[key], obj);
}

function getFirstMetric(campaign, paths) {
  for (const path of paths) {
    const value = getValueByPath(campaign, path);

    if (Array.isArray(value)) {
      return value.length;
    }

    if (value !== undefined && value !== null && value !== "") {
      return safeNumber(value);
    }
  }

  return null;
}

function getItemDate(item) {
  return safeDate(
    item?.sentAt ||
      item?.sentDate ||
      item?.scheduledAt ||
      item?.completedAt ||
      item?.createdAt ||
      item?.dateCreated ||
      item?.updatedAt ||
      item?.date
  );
}

function getCampaignName(campaign) {
  return (
    campaign?.name ||
    campaign?.title ||
    campaign?.campaignName ||
    campaign?.newsletterName ||
    "email sender"
  );
}

function getCampaignSubject(campaign) {
  return (
    campaign?.subject ||
    campaign?.emailSubject ||
    campaign?.campaignSubject ||
    campaign?.settings?.subject ||
    "Subject not available"
  );
}

function getCampaignStatus(campaign) {
  return String(campaign?.status || campaign?.state || "sent").toLowerCase();
}

function getCampaignSent(campaign) {
  const value = getFirstMetric(campaign, [
    "sent",
    "sentCount",
    "emailsSent",
    "totalSent",
    "recipientCount",
    "recipientsCount",
    "stats.sent",
    "metrics.sent",
    "analytics.sent",
    "report.sent",
    "performance.sent",
    "recipients",
    "selectedContacts",
    "sentContacts",
    "sentEmails"
  ]);

  return value !== null ? value : 0;
}

function getCampaignOpened(campaign) {
  const sent = getCampaignSent(campaign);

  const value = getFirstMetric(campaign, [
    "opened",
    "open",
    "opens",
    "openCount",
    "emailsOpened",
    "openedCount",
    "uniqueOpens",
    "uniqueOpened",
    "stats.opened",
    "stats.opens",
    "metrics.opened",
    "metrics.opens",
    "analytics.opened",
    "analytics.opens",
    "report.opened",
    "report.opens",
    "performance.opened",
    "performance.opens",
    "openedContacts",
    "openedEmails",
    "openEvents"
  ]);

  if (value !== null) return value;

  if (campaign?.openRate !== undefined) {
    return Math.round((sent * safeNumber(campaign.openRate)) / 100);
  }

  if (campaign?.stats?.openRate !== undefined) {
    return Math.round((sent * safeNumber(campaign.stats.openRate)) / 100);
  }

  if (campaign?.metrics?.openRate !== undefined) {
    return Math.round((sent * safeNumber(campaign.metrics.openRate)) / 100);
  }

  return 0;
}

function getCampaignClicked(campaign) {
  const sent = getCampaignSent(campaign);

  const value = getFirstMetric(campaign, [
    "clicked",
    "clicks",
    "clickCount",
    "linksClicked",
    "emailsClicked",
    "clickedCount",
    "uniqueClicks",
    "uniqueClicked",
    "stats.clicked",
    "stats.clicks",
    "stats.linksClicked",
    "metrics.clicked",
    "metrics.clicks",
    "metrics.linksClicked",
    "analytics.clicked",
    "analytics.clicks",
    "analytics.linksClicked",
    "report.clicked",
    "report.clicks",
    "report.linksClicked",
    "performance.clicked",
    "performance.clicks",
    "clickedContacts",
    "clickedEmails",
    "clickEvents"
  ]);

  if (value !== null) return value;

  if (campaign?.clickRate !== undefined) {
    return Math.round((sent * safeNumber(campaign.clickRate)) / 100);
  }

  if (campaign?.stats?.clickRate !== undefined) {
    return Math.round((sent * safeNumber(campaign.stats.clickRate)) / 100);
  }

  if (campaign?.metrics?.clickRate !== undefined) {
    return Math.round((sent * safeNumber(campaign.metrics.clickRate)) / 100);
  }

  return 0;
}

function getCampaignUnsubscribed(campaign) {
  const value = getFirstMetric(campaign, [
    "unsubscribed",
    "unsubscribeCount",
    "unsubscribers",
    "stats.unsubscribed",
    "metrics.unsubscribed",
    "analytics.unsubscribed",
    "report.unsubscribed",
    "unsubscribedContacts"
  ]);

  return value !== null ? value : 0;
}

function getCampaignBounced(campaign) {
  const value = getFirstMetric(campaign, [
    "bounced",
    "bounceCount",
    "bounces",
    "stats.bounced",
    "metrics.bounced",
    "analytics.bounced",
    "report.bounced",
    "bouncedContacts"
  ]);

  return value !== null ? value : 0;
}

function getOpenRate(campaign) {
  const sent = getCampaignSent(campaign);
  if (!sent) return 0;

  return (getCampaignOpened(campaign) / sent) * 100;
}

function getClickRate(campaign) {
  const sent = getCampaignSent(campaign);
  if (!sent) return 0;

  return (getCampaignClicked(campaign) / sent) * 100;
}

function getCampaignPreview(campaign) {
  return (
    campaign?.html ||
    campaign?.content ||
    campaign?.emailHtml ||
    campaign?.templateHtml ||
    campaign?.body ||
    campaign?.designHtml ||
    ""
  );
}

function getFallbackPreviewHtml(campaign) {
  return `
    <div style="font-family: Arial, sans-serif; padding: 28px; color: #111; background: #fff; min-height: 260px;">
      <h2 style="margin-top: 0;">Welcome to Our Community!</h2>
      <p>This is a preview of ${getCampaignName(campaign)}.</p>
      <p>If your backend stores campaign HTML/content, it will show here automatically.</p>
      <button style="background:#111;color:#fff;border:0;border-radius:20px;padding:10px 18px;">Read more</button>
    </div>
  `;
}

function getClickedLinks(campaign) {
  const links =
    campaign?.clickedLinks ||
    campaign?.links ||
    campaign?.report?.clickedLinks ||
    campaign?.analytics?.clickedLinks ||
    campaign?.stats?.clickedLinks ||
    [];

  if (!Array.isArray(links)) return [];

  return links.map((link, index) => ({
    id: link.id || link._id || index + 1,
    url: link.url || link.href || link.link || "No link",
    clicks: safeNumber(link.clicks || link.clicked || link.count || 0)
  }));
}

function getRecipients(campaign) {
  const recipients =
    campaign?.recipients ||
    campaign?.selectedContacts ||
    campaign?.sentContacts ||
    campaign?.contacts ||
    [];

  if (!Array.isArray(recipients)) return [];

  return recipients.map((recipient, index) => {
    if (typeof recipient === "string") {
      return {
        id: index + 1,
        name: "-",
        email: recipient,
        status: "sent"
      };
    }

    return {
      id: recipient._id || recipient.id || index + 1,
      name:
        recipient.name ||
        `${recipient.firstName || ""} ${recipient.lastName || ""}`.trim() ||
        "-",
      email: recipient.email || "-",
      status: recipient.status || "sent"
    };
  });
}

async function fetchJson(url, fallback = []) {
  try {
    const response = await fetch(url);

    if (!response.ok) return fallback;

    const data = await response.json();

    if (Array.isArray(data)) return data;
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.contacts)) return data.contacts;
    if (Array.isArray(data.campaigns)) return data.campaigns;
    if (Array.isArray(data.newsletters)) return data.newsletters;
    if (Array.isArray(data.items)) return data.items;

    return fallback;
  } catch (error) {
    console.log(`Dashboard fetch failed: ${url}`, error.message);
    return fallback;
  }
}

function buildCalendarDays(monthDate) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const startOffset = firstDay.getDay();
  const totalDays = lastDay.getDate();

  const days = [];

  for (let i = 0; i < startOffset; i++) {
    days.push(null);
  }

  for (let day = 1; day <= totalDays; day++) {
    days.push(new Date(year, month, day));
  }

  return days;
}

function CalendarMonth({ monthDate, range, onDayClick }) {
  const days = buildCalendarDays(monthDate);

  return (
    <div className="sx-calendar-month">
      <div className="sx-calendar-month-title">
        {monthDate.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric"
        })}
      </div>

      <div className="sx-calendar-weekdays">
        {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
          <span key={`${day}-${index}`}>{day}</span>
        ))}
      </div>

      <div className="sx-calendar-days">
        {days.map((day, index) => {
          if (!day) {
            return <span key={`blank-${index}`}></span>;
          }

          const selected =
            isSameDay(day, range.start) ||
            isSameDay(day, range.end) ||
            isBetween(day, range.start, range.end);

          return (
            <button
              type="button"
              key={day.toISOString()}
              className={selected ? "selected" : ""}
              onClick={() => onDayClick(day)}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DateRangePicker({
  selectedPreset,
  customRange,
  activeRange,
  onPresetChange,
  onCustomChange,
  onClose
}) {
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date(activeRange.end);
    d.setDate(1);
    return d;
  });

  useEffect(() => {
    const d = new Date(activeRange.end);
    d.setDate(1);
    setViewMonth(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPreset]);

  useEffect(() => {
    function handleOutside(e) {
      if (e.target.closest && e.target.closest(".sx-dashboard-date-wrap")) return;
      onClose();
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [onClose]);

  const endMonth = new Date(viewMonth);
  endMonth.setDate(1);

  const startMonth = new Date(endMonth);
  startMonth.setMonth(endMonth.getMonth() - 1);

  function handleDayClick(day) {
    const clickedStart = new Date(day);
    clickedStart.setHours(0, 0, 0, 0);

    const clickedEnd = new Date(day);
    clickedEnd.setHours(23, 59, 59, 999);

    if (!customRange?.start || customRange?.end) {
      onCustomChange({
        start: clickedStart,
        end: null
      });
      return;
    }

    if (clickedEnd < customRange.start) {
      onCustomChange({
        start: clickedStart,
        end: customRange.start
      });
    } else {
      onCustomChange({
        start: customRange.start,
        end: clickedEnd
      });
    }
  }

  const visibleRange = customRange?.start
    ? {
        start: customRange.start,
        end: customRange.end || customRange.start
      }
    : activeRange;

  return (
    <div className="sx-date-picker-panel">
      <div className="sx-date-presets">
        {DATE_PRESETS.map((preset) => (
          <button
            type="button"
            key={preset.label}
            className={selectedPreset === preset.label && !customRange ? "active" : ""}
            onClick={() => onPresetChange(preset)}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="sx-date-calendars">
        <div className="sx-calendar-actions">
          <button type="button" onClick={() => setViewMonth((m) => { const n = new Date(m); n.setMonth(m.getMonth() - 1); return n; })}>‹</button>
          <button type="button" onClick={() => setViewMonth((m) => { const n = new Date(m); n.setMonth(m.getMonth() + 1); return n; })}>›</button>
        </div>

        <div className="sx-calendar-grid">
          <CalendarMonth
            monthDate={startMonth}
            range={visibleRange}
            onDayClick={handleDayClick}
          />

          <CalendarMonth
            monthDate={endMonth}
            range={visibleRange}
            onDayClick={handleDayClick}
          />
        </div>

        <div className="sx-calendar-footer">
          <span>
            {formatDate(visibleRange.start)} - {formatDate(visibleRange.end)}
          </span>

          <button type="button" onClick={onClose}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

function EmailPreview({ campaign, small }) {
  const preview = getCampaignPreview(campaign) || getFallbackPreviewHtml(campaign);

  return (
    <iframe
      title={`${getCampaignName(campaign)} preview`}
      className={small ? "sx-email-preview-frame small" : "sx-email-preview-frame"}
      srcDoc={preview}
    />
  );
}

function EngagementBar({ label, value, maxValue }) {
  const height = maxValue
    ? Math.max(8, (safeNumber(value) / safeNumber(maxValue)) * 170)
    : 0;

  return (
    <div>
      <span>{label}</span>
      <i style={{ height: `${height}px` }}></i>
      <b>{formatNumber(value)}</b>
    </div>
  );
}

function ChartBar({ day, maxValue }) {
  const height = day.value === 0 ? 0 : Math.max(12, (day.value / maxValue) * 170);

  return (
    <div className="dashboard-bar-item">
      <div
        className="dashboard-bar"
        style={{ height: `${height}px` }}
        title={`${day.label}: ${day.value} contacts`}
      ></div>
      <span>{day.label}</span>
    </div>
  );
}

function CampaignReport({ campaign, onBack, initialTab = "Overview" }) {
  const [tab, setTab] = useState(initialTab);

  const sent = getCampaignSent(campaign);
  const opened = getCampaignOpened(campaign);
  const clicked = getCampaignClicked(campaign);
  const unsubscribed = getCampaignUnsubscribed(campaign);
  const bounced = getCampaignBounced(campaign);

  const clickedLinks = getClickedLinks(campaign);
  const recipients = getRecipients(campaign);

  const cards = [
    { label: "Sent", value: sent, sub: "Total sent emails" },
    { label: "Opened", value: opened, sub: `${getOpenRate(campaign).toFixed(2)}%` },
    {
      label: "Links clicked",
      value: clicked,
      sub: `${getClickRate(campaign).toFixed(2)}%`
    },
    { label: "Unsubscribed", value: unsubscribed, sub: "Total unsubscribers" },
    { label: "Bounced", value: bounced, sub: "Failed deliveries" }
  ];

  return (
    <section className="campaign-report-page">
      <div className="campaign-report-top">
        <div>
          <div className="breadcrumb-line">
            <button type="button" onClick={onBack}>
              ‹ Dashboard
            </button>
            <span>/</span>
            <strong>{getCampaignName(campaign)}</strong>
          </div>

          <p className="campaign-time">Sent: {formatDateTime(getItemDate(campaign))}</p>
        </div>

        <div className="campaign-detail-actions">
          <button type="button" onClick={() => window.print()}>
            Print report
          </button>
          <button type="button" onClick={onBack}>
            Close
          </button>
        </div>
      </div>

      <div className="nl-detail-tabs campaign-report-tabs">
        {["Overview", "Performance", "Recipients"].map((item) => (
          <button
            type="button"
            key={item}
            className={tab === item ? "active" : ""}
            onClick={() => setTab(item)}
          >
            {item}
          </button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="campaign-report-summary">
          <div className="campaign-info-panel">
            <h3>Campaign details</h3>
            <p>
              <b>Name:</b> {getCampaignName(campaign)}
            </p>
            <p>
              <b>Subject:</b> {getCampaignSubject(campaign)}
            </p>
            <p>
              <b>Status:</b> {getCampaignStatus(campaign) || "sent"}
            </p>

            <h3>Email Preview</h3>
            <div className="campaign-preview-paper small">
              <EmailPreview campaign={campaign} />
            </div>
          </div>

          <div className="campaign-report-right">
            <div className="performance-cards report-cards">
              {cards.map((card) => (
                <div key={card.label}>
                  <span>{card.label}</span>
                  <b>{formatNumber(card.value)}</b>
                  <small>{card.sub}</small>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "Performance" && (
        <>
          <div className="performance-cards report-cards">
            {cards.map((card) => (
              <div key={card.label}>
                <span>{card.label}</span>
                <b>{formatNumber(card.value)}</b>
                <small>{card.sub}</small>
              </div>
            ))}
          </div>

          <section className="performance-chart-card">
            <div>
              <h3>Engagement</h3>
              <div className="date-box">{formatDate(getItemDate(campaign))}</div>
            </div>

            <div className="sx-engagement-chart">
              <EngagementBar label="Sent" value={sent} maxValue={sent} />
              <EngagementBar label="Opened" value={opened} maxValue={sent} />
              <EngagementBar label="Clicked" value={clicked} maxValue={sent} />
            </div>
          </section>

          <section className="performance-card">
            <h3>Clicked links</h3>

            {clickedLinks.length ? (
              clickedLinks.map((link) => (
                <div className="clicked-link-row" key={link.id}>
                  <span>{link.url}</span>
                  <b>{formatNumber(link.clicks)}</b>
                </div>
              ))
            ) : (
              <p className="muted">No clicked link data found for this campaign.</p>
            )}
          </section>
        </>
      )}

      {tab === "Recipients" && (
        <section className="contacts-table-card report-recipient-card">
          <div className="contacts-table-toolbar">
            <p>Recipients {recipients.length ? `(${recipients.length})` : ""}</p>
          </div>

          <MobileScrollArea className="contacts-table-scroll" ariaLabel="campaign recipients table">
            <table className="contacts-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {recipients.length ? (
                  recipients.map((recipient) => (
                    <tr key={recipient.id}>
                      <td>{recipient.name}</td>
                      <td>{recipient.email}</td>
                      <td>{recipient.status}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3">No recipient data found for this campaign.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </MobileScrollArea>
        </section>
      )}
    </section>
  );
}

export default function DashboardPage({ onOpenNewsletterPerformance, onCreateNewsletter } = {}) {
  const [contacts, setContacts] = useState(() => readCache("dash_contacts", []));
  // Seed from cache so the Dashboard paints instantly (no loading flash). The
  // stale-total problem (deleted campaigns lingering in the cached count) is
  // prevented by pruning the "dash_campaigns" cache whenever a campaign is
  // deleted on the Newsletters page, so this cache only ever holds valid rows.
  const [campaigns, setCampaigns] = useState(() => readCache("dash_campaigns", []));
  const [loading, setLoading] = useState(false);

  // Local (user-timezone) YYYY-MM-DD, so a time just after local midnight maps to
  // today's date and not yesterday (which raw .toISOString() would give).
  const fmtISO = (d) => {
    const x = new Date(d);
    return new Date(x.getTime() - x.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10);
  };
  const _dashInitEnd = new Date();
  const _dashInitStart = new Date();
  const [rangeStart, setRangeStart] = useState(fmtISO(_dashInitStart));
  const [rangeEnd, setRangeEnd] = useState(fmtISO(_dashInitEnd));
  const [activePreset, setActivePreset] = useState("Today");
  const [analyticsSeries, setAnalyticsSeries] = useState(() =>
    readCache(`dash_series_${fmtISO(_dashInitStart)}_${fmtISO(_dashInitEnd)}`, {
      newContactsSeries: [],
      engagementSeries: []
    })
  );

  const [reportCampaign, setReportCampaign] = useState(null);
  const [reportInitialTab, setReportInitialTab] = useState("Overview");

  const activeRange = {
    start: new Date(rangeStart + "T00:00:00"),
    end: new Date(rangeEnd + "T23:59:59")
  };

  const DASH_PRESETS = ["Today", "Yesterday", "This month", "Last month", "All"];

  function applyPreset(label) {
    const e = new Date();
    const s = new Date();
    if (label === "Today") { /* both today */ }
    else if (label === "Yesterday") { s.setDate(s.getDate() - 1); e.setDate(e.getDate() - 1); }
    else if (label === "This month") { s.setDate(1); }
    else if (label === "Last month") { s.setMonth(s.getMonth() - 1, 1); e.setDate(0); }
    else if (label === "All") { s.setFullYear(2000, 0, 1); }
    setRangeStart(fmtISO(s));
    setRangeEnd(fmtISO(e));
    setActivePreset(label);
  }

  useEffect(() => {
    let active = true;
    const key = `dash_series_${rangeStart}_${rangeEnd}`;
    (async () => {
      // NOTE: this endpoint returns an OBJECT ({ newContactsSeries, engagementSeries, totals }),
      // not an array. fetchJson only unwraps array responses, so use a raw fetch here.
      let data = { newContactsSeries: [], engagementSeries: [] };
      try {
        const res = await fetch(`${API_URL}/api/analytics/audience?start=${rangeStart}&end=${rangeEnd}`);
        if (res.ok) data = await res.json();
      } catch (err) {}
      if (!active) return;
      const next = {
        newContactsSeries: Array.isArray(data.newContactsSeries) ? data.newContactsSeries : [],
        engagementSeries: Array.isArray(data.engagementSeries) ? data.engagementSeries : []
      };
      setAnalyticsSeries(next);
      writeCache(key, next);
    })();
    return () => {
      active = false;
    };
  }, [rangeStart, rangeEnd]);

  useEffect(() => {
    let mounted = true;

    async function loadDashboard() {
      const [contactsData, campaignsData, newslettersData] = await Promise.all([
        fetchJson(`${API_URL}/api/contacts`, []),
        fetchJson(`${API_URL}/api/campaigns`, []),
        fetchJson(`${API_URL}/api/newsletters`, [])
      ]);

      if (!mounted) return;

      const mergedCampaigns = [
        ...(Array.isArray(campaignsData) ? campaignsData : []),
        ...(Array.isArray(newslettersData) ? newslettersData : [])
      ];

      // Only ever show this user's own real data. No mock/demo fallback, so a
      // brand-new user sees empty states (not another user's or fake data).
      const nextContacts = Array.isArray(contactsData) ? contactsData : [];
      const nextCampaigns = mergedCampaigns;
      setContacts(nextContacts);
      setCampaigns(nextCampaigns);
      writeCache("dash_contacts", nextContacts);
      writeCache("dash_campaigns", nextCampaigns);
    }

    loadDashboard();

    return () => {
      mounted = false;
    };
  }, []);

  const dashboardData = useMemo(() => {
    function isInRange(item) {
      const itemDate = getItemDate(item);
      return itemDate >= activeRange.start && itemDate <= activeRange.end;
    }

    const contactsInRange = contacts.filter(isInRange);
    const campaignsInRange = campaigns.filter(isInRange);

    const emailsSent = campaignsInRange.reduce(
      (total, campaign) => total + getCampaignSent(campaign),
      0
    );

    const emailsOpened = campaignsInRange.reduce(
      (total, campaign) => total + getCampaignOpened(campaign),
      0
    );

    const linksClicked = campaignsInRange.reduce(
      (total, campaign) => total + getCampaignClicked(campaign),
      0
    );

    const dailyContactsMap = contactsInRange.reduce((acc, contact) => {
      const key = getItemDate(contact).toISOString().slice(0, 10);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const daysDiff = Math.max(
      1,
      Math.ceil((activeRange.end - activeRange.start) / (1000 * 60 * 60 * 24))
    );

    const totalBars = daysDiff <= 7 ? 7 : 6;
    const step = Math.max(1, Math.floor(daysDiff / totalBars));

    const chartDays = [];

    for (let i = totalBars - 1; i >= 0; i--) {
      const currentDate = new Date(activeRange.end);
      currentDate.setDate(activeRange.end.getDate() - i * step);

      const barStartDate = new Date(currentDate);
      barStartDate.setDate(currentDate.getDate() - step + 1);

      const value = Object.entries(dailyContactsMap).reduce(
        (sum, [dateKey, count]) => {
          const date = new Date(dateKey);
          return date >= barStartDate && date <= currentDate ? sum + count : sum;
        },
        0
      );

      chartDays.push({
        key: currentDate.toISOString().slice(0, 10),
        label: currentDate.toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit"
        }),
        value
      });
    }

    const maxChartValue = Math.max(...chartDays.map((day) => day.value), 1);

    const sentCampaigns = [...campaigns]
      .filter((campaign) => {
        const status = getCampaignStatus(campaign);

        return (
          getCampaignSent(campaign) > 0 ||
          status === "sent" ||
          status === "completed" ||
          status === "delivered"
        );
      })
      .sort((a, b) => getItemDate(b) - getItemDate(a));

    return {
      newContacts: contactsInRange.length,
      emailsSent,
      emailsOpened,
      linksClicked,
      chartDays,
      maxChartValue,
      // Show the user's most recent real campaign (sent first, else newest).
      // If they have none, the card renders its empty "No campaigns" state.
      lastCampaign: sentCampaigns[0] || campaigns[0] || null
    };
  }, [contacts, campaigns, activeRange.start, activeRange.end]);

  if (reportCampaign) {
    return (
      <CampaignReport
        campaign={reportCampaign}
        initialTab={reportInitialTab}
        onBack={() => setReportCampaign(null)}
      />
    );
  }

  const dateRangeText = `${formatDate(activeRange.start)} - ${formatDate(
    activeRange.end
  )}`;

  const lastCampaign = dashboardData.lastCampaign;

  const barStyle = (h) => ({ height: `${Number(h) || 0}%` });
  const newSeries = Array.isArray(analyticsSeries.newContactsSeries) ? analyticsSeries.newContactsSeries : [];
  const engSeries = Array.isArray(analyticsSeries.engagementSeries) ? analyticsSeries.engagementSeries : [];
  const safeSeriesMax = (arr) => {
    const m = Math.max(0, ...arr.map((n) => Number(n) || 0));
    return m > 0 ? m : 1;
  };
  const maxNew = safeSeriesMax(newSeries.flatMap((p) => [p.added, p.unsubscribed]));
  const maxEng = safeSeriesMax(engSeries.flatMap((p) => [p.sent, p.opened, p.clicked, p.bounced]));

  return (
    <section className="dashboard-page">
      <div className="title-row">
        <h2>Dashboard</h2>
        <span className="badge">Test-1</span>
      </div>

      <div className="dashboard-head">
        <h3>Overall Performance</h3>

        <div className="analytics-date-controls sx-dashboard-date-wrap">
          <div className="analytics-presets">
            {DASH_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                className={activePreset === p ? "active" : ""}
                onClick={() => applyPreset(p)}
              >
                {p}
              </button>
            ))}
          </div>

          <div className="analytics-date-range">
            <label>
              From
              <input
                type="date"
                value={rangeStart}
                max={rangeEnd}
                onChange={(e) => { setRangeStart(e.target.value); setActivePreset(""); }}
              />
            </label>
            <label>
              To
              <input
                type="date"
                value={rangeEnd}
                min={rangeStart}
                onChange={(e) => { setRangeEnd(e.target.value); setActivePreset(""); }}
              />
            </label>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="dashboard-loading">Loading dashboard...</div>
      ) : (
        <>
          <div className="stats-grid dashboard-stats-grid">
            <div className="stat-card">
              <p>New Contacts</p>
              <h1>{formatNumber(dashboardData.newContacts)}</h1>
            </div>

            <div className="stat-card">
              <p>Emails sent</p>
              <h1>{formatNumber(dashboardData.emailsSent)}</h1>
            </div>

            <div className="stat-card">
              <p>Emails Opened</p>
              <h1>{formatNumber(dashboardData.emailsOpened)}</h1>
            </div>

            <div className="stat-card">
              <p>Email Links Clicked</p>
              <h1>{formatNumber(dashboardData.linksClicked)}</h1>
            </div>
          </div>

          <div className="analytics-grid dashboard-analytics-grid">
            <section className="analytics-card large">
              <div className="analytics-card-head">
                <h3>New Contacts</h3>
                <span className="analytics-card-sub">Daily new and unsubscribed contacts</span>
              </div>
              {newSeries.length ? (
                <div className="mini-bar-chart">
                  {newSeries.map((pt) => (
                    <div
                      key={pt.date}
                      className="mini-bar-col"
                      title={`${pt.date}: ${pt.added} added, ${pt.unsubscribed} unsubscribed`}
                    >
                      <div className="mini-bar added" style={barStyle((pt.added / maxNew) * 100)}></div>
                      <div className="mini-bar unsub" style={barStyle((pt.unsubscribed / maxNew) * 100)}></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="analytics-empty">No data for this period</div>
              )}
            </section>

            <section className="analytics-card large">
              <div className="analytics-card-head">
                <h3>Engagement</h3>
                <span className="analytics-card-sub">Opens, clicks, sent and bounced over time</span>
              </div>
              {engSeries.length ? (
                <div className="mini-bar-chart">
                  {engSeries.map((pt) => (
                    <div
                      key={pt.date}
                      className="mini-bar-col"
                      title={`${pt.date}: ${pt.sent} sent, ${pt.opened} opened, ${pt.clicked} clicked, ${pt.bounced || 0} bounced`}
                    >
                      <div className="mini-bar sent" style={barStyle((pt.sent / maxEng) * 100)}></div>
                      <div className="mini-bar opened" style={barStyle((pt.opened / maxEng) * 100)}></div>
                      <div className="mini-bar clicked" style={barStyle((pt.clicked / maxEng) * 100)}></div>
                      <div className="mini-bar bounced" style={barStyle(((pt.bounced || 0) / maxEng) * 100)}></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="analytics-empty">No data for this period</div>
              )}
            </section>
          </div>

          <section className="last-campaign-card">
            <h3>Last Sent Campaign</h3>
            <button
              type="button"
              className="yellow-btn lc-create-btn"
              onClick={() => { if (onCreateNewsletter) onCreateNewsletter(); }}
            >
              ＋ Create Newsletter
            </button>

            {lastCampaign ? (
              <>
              <div className="last-campaign-main">
                <div className="last-campaign-thumb">
                  <EmailPreview campaign={lastCampaign} small />
                </div>

                <div className="last-campaign-info">
                  <h4>{getCampaignName(lastCampaign)}</h4>
                  <p>{getCampaignSubject(lastCampaign)}</p>
                  <small>Sent: {formatDateTime(getItemDate(lastCampaign))}</small>
                </div>

                <div className="last-campaign-actions">
                  <button
                    type="button"
                    className="view-report-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onOpenNewsletterPerformance && lastCampaign?._id) {
                        onOpenNewsletterPerformance(lastCampaign._id);
                      } else {
                        setReportInitialTab("Performance");
                        setReportCampaign(lastCampaign);
                      }
                    }}
                  >
                    View report
                  </button>

                </div>
              </div>

              <div className="last-campaign-stats">
                <div
                  className="lc-stat-clickable"
                  onClick={() => {
                    if (onOpenNewsletterPerformance && lastCampaign?._id) {
                      onOpenNewsletterPerformance(lastCampaign._id, "sent");
                    }
                  }}
                >
                  <p>Sent</p>
                  <h2>{formatNumber(getCampaignSent(lastCampaign))}</h2>
                </div>

                <div
                  className="lc-stat-clickable"
                  onClick={() => {
                    if (onOpenNewsletterPerformance && lastCampaign?._id) {
                      onOpenNewsletterPerformance(lastCampaign._id, "open");
                    }
                  }}
                >
                  <p>Opened</p>
                  <h2>{formatNumber(getCampaignOpened(lastCampaign))}</h2>
                  <small>{getOpenRate(lastCampaign).toFixed(2)}%</small>
                </div>

                <div
                  className="lc-stat-clickable"
                  onClick={() => {
                    if (onOpenNewsletterPerformance && lastCampaign?._id) {
                      onOpenNewsletterPerformance(lastCampaign._id, "click");
                    }
                  }}
                >
                  <p>Links clicked</p>
                  <h2>{formatNumber(getCampaignClicked(lastCampaign))}</h2>
                </div>
              </div>
              </>
            ) : (
              <div className="analytics-empty lc-empty">No campaigns sent yet.</div>
            )}
          </section>
        </>
      )}
    </section>
  );
}